/**
 * Action Center Reminders Service
 * Service for sending bulk reminders from the Send Requirement Reminders wizard
 */

import { RequestService } from './RequestService'
import { formatLocalDate } from '../components/Reminders/dateUtils'

export const ActionCenterRemindersService = (functionsBaseUrl) => {
  const { post, handleErrors } = RequestService()

  /**
   * Send bulk reminders via the calendar gateway
   * @param {Object} payload - The payload from buildSelectedPayload()
   * @param {Array} payload.items - Array of requirement items with employers and selected emails
   * @returns {Promise<Object>} Result with success status and details
   */
  const sendBulkReminders = async (payload) => {
    const url = `${functionsBaseUrl}/calendarGateway/api/v1/bulk-reminders`
    
    try {
      const response = await post('sendBulkReminders', url, payload)
      
      if (response?.data?.success) {        
        return response.data.data
      } else {
        throw new Error(response?.data?.message || 'Failed to send bulk reminders')
      }
    } catch (error) {
      handleErrors('sendBulkReminders', error)
      throw error
    }
  }

  /**
   * Flatten and prepare the wizard payload into individual reminder for the API.
   * Each reminder = 1 calendar event (1 requirement × 1 employer with their emails as attendees).
   * 
   * @param {Object} payload - The payload from buildSelectedPayload()
   * @returns {Object} { reminders: [...] } ready to send to the backend
   */
  const preparePayloadForApi = (payload) => {
    const reminders = []

    for (const item of payload.items) {
      for (const employer of item.employers) {
        // Skip if no emails selected
        if (!employer.selectedEmails || employer.selectedEmails.length === 0) {
          continue
        }

        // Parse the reminder date to YYYY-MM-DD
        let reminderDate = ''
        if (item.reminderDateObj instanceof Date) {
          reminderDate = formatLocalDate(item.reminderDateObj)
        } else if (typeof item.reminderDateObj === 'string') {
          const parsed = new Date(item.reminderDateObj)
          reminderDate = isNaN(parsed) ? '' : formatLocalDate(parsed)
        }

        // Parse due date to YYYY-MM-DD
        let dueDate = ''
        if (item.earliestDueDateObj instanceof Date) {
          dueDate = formatLocalDate(item.earliestDueDateObj)
        } else if (typeof item.earliestDueDateObj === 'string') {
          const parsed = new Date(item.earliestDueDateObj)
          dueDate = isNaN(parsed) ? '' : formatLocalDate(parsed)
        }

        // Use customReminderDays for custom date selections (-2), otherwise use reminderBusinessDays
        const businessDays = item.reminderBusinessDays === -2 && item.customReminderDays != null
          ? item.customReminderDays
          : item.reminderBusinessDays

        reminders.push({
          requirementKey: item.key,
          requirementName: item.requirementName,
          eventBody: item.eventBody || '',
          dueDate,
          reminderDate,
          reminderBusinessDays: businessDays,
          employerId: employer.employerId,
          employerName: employer.employerName,
          planYearId: employer.plan_year_id,
          attendees: employer.selectedEmails.map(email => ({ email }))
        })
      }
    }

    return { reminders }
  }

  /**
   * Aggregate flat reminder results (from backend) into grouped format for display.
   * Groups by requirementKey + jobId, so reminders from the same bulk send operation
   * are grouped together, while different send operations show as separate rows.
   * 
   * For reminders created individually (without requirementKey/jobId), falls back to:
   * - requirementKey: name::due_date
   * - groupKey: uses created_at (day only) instead of jobId
   * 
   * @param {Array} details - Flat array of reminder results from backend
   * @returns {Array} Grouped reminders for SentRemindersModal (Action Center mode)
   * 
   * Output structure (per group):
   * {
   *   groupKey, requirementKey, jobId, name, due_date, start_date, created_at,
   *   employers: [{ employerId, employerName, reminderId, calendarLink, attendeesCount, target_emails }],
   *   employersCount, successCount, failedCount
   * }
   */
  const aggregateReminderResults = (details) => {
    if (!Array.isArray(details) || details.length === 0) return []

    const groupMap = new Map()

    for (const item of details) {
      // Build requirementKey: use existing or fallback to name::due_date for individual reminders
      let requirementKey = item.requirementKey
      if (!requirementKey && item.name && item.due_date) {
        requirementKey = `${item.name}::${item.due_date}`
      }
      
      // Skip items that still don't have a valid key
      if (!requirementKey) continue

      // Build groupKey identifier:
      // - For bulk reminders: use jobId
      // - For individual reminders: use created_at (day only, no time)
      let groupIdentifier = item.jobId
      if (!groupIdentifier && item.created_at) {
        // Extract just the date part (YYYY-MM-DD) from created_at
        groupIdentifier = item.created_at.split('T')[0]
      }
      groupIdentifier = groupIdentifier || 'no-job'
      
      const groupKey = `${requirementKey}::${groupIdentifier}`

      if (!groupMap.has(groupKey)) {
        groupMap.set(groupKey, {
          groupKey,
          requirementKey: requirementKey,
          jobId: item.jobId || null,
          name: item.name || '',
          due_date: item.due_date || '',
          start_date: item.start_date || '',
          created_at: item.created_at || '',
          employers: [],
          employerIds: new Set(),
          successCount: 0,
          failedCount: 0
        })
      }

      const group = groupMap.get(groupKey)

      // Accumulate employer data only if not already present (unique by employerId)
      if (!group.employerIds.has(item.employerId)) {
        group.employerIds.add(item.employerId)
      group.employers.push({
        employerId: item.employerId,
        employerName: item.employerName,
        reminderId: item.reminderId || null,
        calendarLink: item.calendarLink || null,
        attendeesCount: item.attendeesCount || 0,
        target_emails: item.target_emails || '',
        success: item.success ?? false,
        error: item.error || null
      })

      // Track success/failure counts
      if (item.success) {
        group.successCount++
      } else {
        group.failedCount++
      }
    }
    }

    // Convert map to array and add employersCount
    return Array.from(groupMap.values()).map(group => ({
      ...group,
      employersCount: group.employers.length
    }))
  }

  return {
    sendBulkReminders,
    preparePayloadForApi,
    aggregateReminderResults
  }
}
